import React, { Component } from 'react';
import {
  inject,
  observer,
} from 'mobx-react';
import {
  toNumber,
  zeroPad,
} from '../../utils/numbers';
import './MixBar.css';
import { withTranslation } from 'react-i18next';

const formatValue = (value, shouldPad) => shouldPad ? zeroPad(value, 2) : value;

@inject('materialStore', 'batchStore', 'settingsStore')
@observer
class MixBar extends Component {
  constructor() {
    super();
    this.state = {
      show: false,
    };
    this.refreshInterval = null;
  }

  componentDidMount() {
    // Force refresh every 500ms in dev mode to see real-time updates
    if (process.env.NODE_ENV === 'development') {
      this.refreshInterval = setInterval(() => {
        this.forceUpdate();
      }, 500);
    }
  }

  componentWillUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  toggle = () => {
    this.setState(prevState => ({
      show: !prevState.show,
    }));
  };

  getMaterial(constituentItemCode, currentBatch) {
    if (!currentBatch || !currentBatch.mixDesign) {
      return null;
    }

    // Find the matching material in mixDesign
    const material = currentBatch.mixDesign.find(m =>
      m.constituentItemCode === constituentItemCode
    );

    return material || null;
  }

  getActualWeight(constituentItemCode, currentBatch) {
    const material = this.getMaterial(constituentItemCode, currentBatch);

    return material ? (material.actualWeight || 0) : 0;
  }

  getMeterRate(constituent, currentBatch) {
    const { materialStore } = this.props;
    const md = materialStore.getConstituentMaterialDetails(constituent);

    return md ? (md.meterRate || 0) : 0;
  }

  render() {
    const { materialStore, batchStore, settingsStore, data, t } = this.props;
    const { show } = this.state;
    const icon = show ? 'fa fa-angle-up' : 'fa fa-angle-down';
    const open = show ? 'mix-status-bar-open' : '';
    const isDevelopment = process.env.NODE_ENV === 'development' || settingsStore.configuration?.showBatchMonitor;
    const hasBatchRunning = batchStore.running && batchStore.running.length > 0;

    return (!data || !data.mix) ? '' : (
      <div className={`mix-status-bar ${open}`} data-e2e-id="mix-bar-status">
        <a className="mix-status-bar--action pull-right" onClick={this.toggle} data-e2e-id="btn-mix-bar-status-hide-list"><i className={icon}/></a>
        <div className="mix-status-bar--mix">
          {t('Mix')}: {data.mix.itemCode}
        </div>
        {show && (
          <table className="mix-design-table">
            <thead>
              <tr>
                <th/>
                <th data-e2e-id="mix-bar-status-quantity">{t('Qty')}</th>
                <th data-e2e-id="mix-bar-status-quantity-in-mix">{t('Mix')}</th>
                {data.dropSize !== 1 &&
                  <th data-e2e-id="mix-bar-status-drop">{t('Drop')}</th>
                }
                {isDevelopment && hasBatchRunning && (
                  <th data-e2e-id="mix-bar-status-actual">{t('Actual')}</th>
                )}
              </tr>
            </thead>
            <tbody data-e2e-id="mix-bar-status-mix-design-table">
              {data.mixDesign.map((v,index) => {
                // If material's graduation or meter rate is > 1, we should pad zeros
                // as per NTEP certification requirements. (00, 06 instead of 1, 6 etc.)
                const shouldPadZeros = (
                  materialStore.getScaleGraduationForConstituent(v) > 1
                  || this.getMeterRate(v, data) > 1
                );

                return (
                  <tr key={v.constituentItemCode} data-e2e-id={`mix-bar-status-table-row-${index}`}>
                    <td data-e2e-id={`mix-bar-status-item-code-${index}`}>
                      {materialStore.getConstituentName(v)}
                    </td>
                    <td data-e2e-id={`mix-bar-status-quantity-${index}`}>{formatValue(v.quantity, shouldPadZeros)}</td>
                    <td data-e2e-id={`mix-bar-status-quantity-in-mix-${index}`}>{formatValue(v.quantityFinal, shouldPadZeros)}</td>
                    {data.dropSize !== 1 &&
                      <td data-e2e-id={`mix-bar-status-drop-${index}`}>{formatValue(toNumber(v.quantityFinal * data.dropSize, 2), shouldPadZeros)}</td>
                    }
                    {isDevelopment && hasBatchRunning && (
                      <td data-e2e-id={`mix-bar-status-actual-${index}`} style={{ fontWeight: this.getActualWeight(v.constituentItemCode, data) > 0 ? 'bold' : 'normal' }}>
                        {formatValue(toNumber(this.getActualWeight(v.constituentItemCode, data), 2), shouldPadZeros)}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  }
}

export default withTranslation()(MixBar);
